import { Box, Divider } from '@citibox-panel/ui';
import type { ConfirmLockSequenceInput, LockCodes } from '@citibox-panel/api-client';
import { useLockCodes } from '../hooks/useLockCodes';
import { LockCodeRails } from './LockCodeRails';

export interface LockCodesSectionProps {
  open: boolean;
  lockId: string;
  deliveryId: string;
  fetchLockCodes: (lockId: string) => Promise<LockCodes>;
  confirmLockSequence: (lockId: string, input: ConfirmLockSequenceInput) => Promise<void>;
}

// The lock-codes block at the top of the «Ver log buzón» disclosure, above the log table: the two
// code tables of the cerradura. Mounted whenever the cerradura has a devices id (even collapsed, so
// the query keeps its cache and observer across a fold→unfold), renders nothing until unfolded —
// the first unfold is the lazy read.
//
// Sibling of `LockerLogSection` and independent of it by design: two queries in one disclosure, so
// a codes failure degrades to the inline strip while the log below keeps working, and vice versa.
//
// It owns the QUERY and the `open` gate, and nothing else. The read's states and the whole
// interaction belong to `LockCodeRails`, which must survive a refetch this section would otherwise
// swap out from under — see the note there.
export function LockCodesSection({
  open,
  lockId,
  deliveryId,
  fetchLockCodes,
  confirmLockSequence,
}: LockCodesSectionProps) {
  // Called before the early return so the query stays mounted while this component is; the return
  // only hides the region.
  const { codes, isPending, isFetching, isError, refetch } = useLockCodes(
    lockId,
    open,
    fetchLockCodes,
  );
  if (!open) return null;
  return (
    <>
      <Box sx={{ px: 2, pt: 2, pb: 2 }}>
        <LockCodeRails
          codes={codes}
          isPending={isPending}
          isFetching={isFetching}
          isError={isError}
          refetch={refetch}
          lockId={lockId}
          deliveryId={deliveryId}
          confirmLockSequence={confirmLockSequence}
        />
      </Box>
      {/* Separates the codes from the log below, which is a different read about a different
          device — the «buzón», not the cerradura. Inset to start on the same vertical as the log
          table's own row hairlines (the log section's `px: 2` plus the table's `px: 1`), so every
          horizontal rule inside the shared box lines up instead of one running wider than the
          rest. Full-bleed was tried first and read as a seam splitting the box in two. */}
      <Divider sx={{ mx: 3 }} />
    </>
  );
}
